import { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'

// GET request for workday summary data
const getWorkdaySummaryData = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/workday-summary?filterDate=${filterDate}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching workday summary data:', error)
    throw error
  }
}

export const useWorkdaySummary = (customFarmId = null, initialDate = null) => {
  const { user } = useAuth()
  const hasFetched = useRef(false)

  // Use custom farmId if provided, otherwise fall back to user.farmId
  const farmId = customFarmId || user?.farmId

  const [workdaySummary, setWorkdaySummary] = useState({
    totalWorkers: 0,
    workersByGroup: [],
    workersInBlock: [],
    farmActivityData: [],
    lastActivitiesData: [],
    filterDate: initialDate || DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    currentDate: DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    loading: true,
    error: null
  })

  const fetchWorkdaySummary = async () => {
    if (!farmId) return

    try {
      setWorkdaySummary(prev => ({ ...prev, loading: true, error: null }))

      const data = await getWorkdaySummaryData(farmId, workdaySummary.filterDate)

      setWorkdaySummary(prev => ({
        ...prev,
        farmActivityData: data.farmActivitySummary || [],
        lastActivitiesData: data.lastActivitiesBoard || [],
        workersByGroup: data.workersByGroup || [],
        workersInBlock: data.workersInBlock || [],
        totalWorkers: data.totalActiveWorkers || 0,
        loading: false
      }))
    } catch (error) {
      console.error('Error fetching workday summary:', error)
      setWorkdaySummary(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load workday summary data'
      }))
    }
  }

  const updateFilterDate = (newDate) => {
    setWorkdaySummary(prev => ({
      ...prev,
      filterDate: newDate
    }))
  }

  // Initial fetch
  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true

    fetchWorkdaySummary()
  }, [farmId])

  // Ensure currentDate is part of state
  useEffect(() => {
    if (!workdaySummary.currentDate) {
      setWorkdaySummary(prev => ({
        ...prev,
        currentDate: workdaySummary.currentDate
      }))
    }
  }, [])

  // Refetch when date changes
  useEffect(() => {
    if (workdaySummary.currentDate && farmId) {
      fetchWorkdaySummary()
    }
  }, [workdaySummary.filterDate, farmId])

  return {
    data: workdaySummary,
    updateFilterDate,
    refetch: fetchWorkdaySummary
  }
}
